import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import {
    ClienteDTO,
    CuotaDTO,
    EncuestaDTO,
    EnvioDTO,
    FunnelDTO,
    GrupoDTO,
    GrupoFiltroDTO,
    InvestigacionDTO,
    MarcadorDTO,
    PaisDTO,
    RespuestaDiaDTO,
    RespuestaEncuesta,
    TagDTO
} from "@DTOS";
import { InvestigacionesAcciones } from "./investigaciones.acciones";
import { InvestigacionesService } from "../../servicesBackend/investigaciones.service";
import { PaginadoState } from "../PaginadoState";
import { Nilable } from "@shared/utils";
import { ClientesAcciones } from "../clientes/clientes.actions";
import { EtiquetasAcciones } from "../etiquetas/etiquetas.acciones";
import { obtenerActualizadores } from "./clases";
import { GruposFiltrosService } from "../../../layout/investigaciones/gruposFiltros.service";
import { AccionEnum } from "@shared/enums";
import { EncuestasService } from "../../../layout/encuestas/encuestas.service";
import { RespuestasService } from "../../../layout/investigaciones/ver-respuestas/respuestas.service";
import { ResultadoActualizacionEnvio } from './clases/actualizador/EnvioActualizador';

const CANTIDAD_INVESTIGACIONES = 21;

export interface IInvestigacionesState {
    totalInvestigaciones: number;
    investigaciones: InvestigacionDTO[];
    investigacionSeleccionada: Nilable<InvestigacionDTO>;
    cuotasInvestigacionSeleccionada: Nilable<CuotaDTO[]>;
    funnelsInvestigacionSeleccionada: Nilable<FunnelDTO[]>;
    marcadoresInvestigacionSeleccionada: Nilable<MarcadorDTO[]>;
    respuestasDiaInvestigacionSeleccionada: Nilable<RespuestaDiaDTO[]>;
    respuestasEncuestaInvestigacionSeleccionada: Nilable<Map<number, RespuestaEncuesta[]>>;
    gruposInvestigacionSeleccionada: Nilable<GrupoDTO[]>;
    envioActualizado: Nilable<EnvioDTO>;
}

@State<IInvestigacionesState>({
    name: "investigaciones",
    defaults: {
        totalInvestigaciones: 0,
        investigaciones: [],
        investigacionSeleccionada: null,
        cuotasInvestigacionSeleccionada: null,
        funnelsInvestigacionSeleccionada: null,
        marcadoresInvestigacionSeleccionada: null,
        respuestasDiaInvestigacionSeleccionada: null,
        respuestasEncuestaInvestigacionSeleccionada: null,
        gruposInvestigacionSeleccionada: null,
        envioActualizado: null
    }
})
@Injectable()
export class InvestigacionesState extends PaginadoState<IInvestigacionesState, InvestigacionDTO> {
    private idInvestigacionesCompletas: number[] = [];
    private soloActivas = true;

    private investigacionNueva: Nilable<InvestigacionDTO>;
    private paisesInvestigacionNueva: Nilable<PaisDTO[]>;

    protected condicionesReinicio = () => this.soloActivas;

    constructor(private investigacionesService: InvestigacionesService, private encuestasService: EncuestasService,
        private gruposFiltroService: GruposFiltrosService, private respuestasService: RespuestasService
    ) {
        super(
            (cantidad, pagina) =>
                this.investigacionesService.obtenerInvestigaciones(pagina, cantidad, this.soloActivas),
            (query, cantidad, pagina) =>
                this.investigacionesService.obtenerInvestigacionesQueryNuevo(query, pagina, cantidad, this.soloActivas)
        );
    }

    @Action(InvestigacionesAcciones.Obtener)
    public async obtenerInvestigaciones(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.Obtener
    ) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        this.soloActivas = action.activas;
        const investigaciones = await this.obtenerCantidad(action.cantidad);
        stateNuevo.totalInvestigaciones = this.obtenerTotal();
        stateNuevo.investigaciones = [
            ...investigaciones.filter(investigacion => this.soloActivas ? investigacion.activado : true)
        ];
        ctx.dispatch(new ClientesAcciones.PedirEntidades(InvestigacionesAcciones.CargarClientes));
        ctx.setState(stateNuevo);
    }

    @Action(InvestigacionesAcciones.ObtenerMas)
    public async obtenerInvestigacionesPagina(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.ObtenerMas
    ) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        await this.cargarPagina(action.cantidad, action.pagina);
        const investigaciones = this.obtenerTodos();
        stateNuevo.investigaciones = [
            ...investigaciones.filter(investigacion => this.soloActivas ? investigacion.activado : true)
        ];
        ctx.dispatch(new ClientesAcciones.PedirEntidades(InvestigacionesAcciones.CargarClientes));
        ctx.setState(stateNuevo);
    }

    @Action(InvestigacionesAcciones.Buscar)
    public async buscarInvestigaciones(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.Buscar
    ) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        this.soloActivas = action.activas;
        this.limpiarResultadosAnteriores();
        const investigaciones = await this.obtenerCantidadQuery(action.termino, action.cantidad);
        stateNuevo.totalInvestigaciones = this.obtenerTotalQuery();
        stateNuevo.investigaciones = [
            ...investigaciones.filter(investigacion => this.soloActivas ? investigacion.activado : true)
        ];
        ctx.dispatch(new ClientesAcciones.PedirEntidades(InvestigacionesAcciones.CargarClientes));
        ctx.setState(stateNuevo);
    }

    @Action(InvestigacionesAcciones.BuscarMas)
    public async obtenerInvestigacionesBuscadasPagina(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.BuscarMas
    ) {
        const stateNuevo = this.obtenerNuevoState(ctx);
        await this.cargarPaginaQuery(action.cantidad, action.pagina);
        const investigaciones = this.obtenerTodosQuery();
        stateNuevo.totalInvestigaciones = this.obtenerTotalQuery();
        stateNuevo.investigaciones = [
            ...investigaciones.filter(investigacion => this.soloActivas ? investigacion.activado : true)
        ];
        ctx.dispatch(new ClientesAcciones.PedirEntidades(InvestigacionesAcciones.CargarClientes));
        ctx.setState(stateNuevo);
    }

    @Action(InvestigacionesAcciones.ObtenerId)
    public async obtenerInvestigacionPorId(ctx: StateContext<any>, action: InvestigacionesAcciones.ObtenerId) {
        const stateNuevo = this.obtenerNuevoState(ctx);
        if (this.idInvestigacionesCompletas.includes(action.id)) {
            stateNuevo.investigacionSeleccionada =
                this.obtenerTodos().find(investigacion => investigacion.id === action.id)!;
            InvestigacionesState.reiniciarExtras(stateNuevo);
            ctx.setState(stateNuevo);
        } else {
            const investigacionCompleta = await this.investigacionesService.obtenerInvestigacion(action.id);
            if (investigacionCompleta != null) {
                if (this.obtenerTodos().length !== 0) {
                    this.idInvestigacionesCompletas.push(investigacionCompleta.id);
                    if (this.obtenerTodos().every(investigacion => investigacion.id !== action.id)) {
                        this.agregarEntidad(investigacionCompleta);
                    }
                    const investigaciones = this.actualizarEntidades(investigacion =>
                        investigacion.id === action.id ? investigacionCompleta : investigacion
                    );
                    stateNuevo.investigaciones = [
                        ...investigaciones.filter(investigacion =>
                            this.soloActivas ? investigacion.activado : true)
                    ];
                }
            }
            stateNuevo.investigacionSeleccionada = investigacionCompleta;
            InvestigacionesState.reiniciarExtras(stateNuevo);
            ctx.setState(stateNuevo);
            ctx.dispatch(new ClientesAcciones.PedirEntidades(InvestigacionesAcciones.CargarClientes));
        }
    }

    @Action(InvestigacionesAcciones.ObtenerCuotas)
    public async obtenerCuotas(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.ObtenerCuotas
    ) {
        const cuotas = await this.investigacionesService.obtenerCuotas(action.investigacion);
        const nuevoState = this.obtenerNuevoState(ctx);
        nuevoState.cuotasInvestigacionSeleccionada = cuotas;
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.ObtenerFunnels)
    public async obtenerFunnels(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.ObtenerFunnels
    ) {
        const funnels = await this.investigacionesService.obtenerFunnels(action.investigacion, action.envio);
        const nuevoState = this.obtenerNuevoState(ctx);
        nuevoState.funnelsInvestigacionSeleccionada = funnels;
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.ObtenerMarcadores)
    public async obtenerMarcadores(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.ObtenerFunnels
    ) {
        const marcadores = await this.investigacionesService.obtenerMarcadores(action.investigacion, action.envio);
        const nuevoState = this.obtenerNuevoState(ctx);
        nuevoState.marcadoresInvestigacionSeleccionada = marcadores;
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.ObtenerRespuestasDia)
    public async obtenerRespuestasDia(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.ObtenerRespuestasDia
    ) {
        const respuestasDia = await this.investigacionesService.obtenerRespuestaDia(action.investigacion, action.envio);
        const nuevoState = this.obtenerNuevoState(ctx);
        nuevoState.respuestasDiaInvestigacionSeleccionada = respuestasDia;
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.ObtenerRespuestas)
    public async obtenerRespuestas(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.ObtenerRespuestas
    ) {
        const respuestasEncuesta = await this.investigacionesService.obtenerRespuestas(
            action.investigacion, action.encuesta, 6, 0
        );
        const nuevoState = this.obtenerNuevoState(ctx);
        const respuestas = new Map<number, RespuestaEncuesta[]>(
            nuevoState.respuestasEncuestaInvestigacionSeleccionada?.entries() ?? []
        );
        respuestas.set(action.encuesta.id, respuestasEncuesta);
        nuevoState.respuestasEncuestaInvestigacionSeleccionada = respuestas;
        ctx.setState(nuevoState);
        this.iniciarFetchRespuestasWorker(ctx, { ...nuevoState }, action.investigacion, action.encuesta, 6);
    }

    @Action(InvestigacionesAcciones.ObtenerGrupos)
    public async obtenerGrupos(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.ObtenerGrupos
    ) {
        const grupos = await this.investigacionesService.obtenerGrupos(action.investigacion);
        const nuevoState = this.obtenerNuevoState(ctx);
        nuevoState.gruposInvestigacionSeleccionada = grupos;
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.CargarClientes)
    public cargarClientes(ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.CargarClientes) {
        const nuevoState = this.obtenerNuevoState(ctx);

        const investigaciones = this.actualizarEntidades(investigacion => {
            const clone = new InvestigacionDTO(investigacion.toJSON());
            clone.formatear();
            clone.cliente = action.clientes.find(cliente => cliente.id === investigacion.cliente.id) ??
                new ClienteDTO({ id: investigacion.cliente.id });
            return clone;
        });
        nuevoState.investigaciones = [
            ...investigaciones.filter(investigacion => this.soloActivas ? investigacion.activado : true)
        ];
        if (nuevoState.investigacionSeleccionada != null) {
            nuevoState.investigacionSeleccionada = investigaciones.find(investigacion =>
                investigacion.id === nuevoState.investigacionSeleccionada!.id
            )!;
        }

        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.Agregar)
    public async agregar(ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.Agregar) {
        const nuevoState = this.obtenerNuevoState(ctx);
        const investigacionNueva = await this.investigacionesService.agregarInvestigacion(action.investigacion);
        this.agregarEntidad(investigacionNueva);
        this.investigacionNueva = investigacionNueva;
        this.paisesInvestigacionNueva = action.paises;

        const investigaciones = await this.obtenerCantidad(CANTIDAD_INVESTIGACIONES);
        nuevoState.investigaciones = [
            ...investigaciones.filter(investigacion => this.soloActivas ? investigacion.activado : true)
        ];
        nuevoState.investigacionSeleccionada = investigacionNueva;

        ctx.dispatch(new EtiquetasAcciones.PedirEntidades(InvestigacionesAcciones.CargarEtiquetas));
        ctx.dispatch(new ClientesAcciones.PedirEntidades(InvestigacionesAcciones.CargarClientes));
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.Actualizar)
    public async actualizar(ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.Actualizar) {
        const state = ctx.getState();
        const investigacion = action.investigacion ?? state.investigacionSeleccionada!;

        const actualizadorEncontrado = obtenerActualizadores(
            this.investigacionesService, this.gruposFiltroService, this.respuestasService
        ).find(actualizador => actualizador.esActualizador(action.datos));
        if (actualizadorEncontrado == null) {
            console.error('No se encontró actualizador para los datos:', action.datos);
            return;
        }

        const resultado = await actualizadorEncontrado.actualizar(investigacion, action.datos);

        if (resultado && typeof resultado === 'object' && 'investigacion' in resultado && 'envio' in resultado) {
            const resultadoActualizado = resultado as ResultadoActualizacionEnvio;
            
            if (resultadoActualizado.investigacion != null) {
                const investigaciones = state.investigaciones.map(inv =>
                    inv.id === resultadoActualizado.investigacion.id ? resultadoActualizado.investigacion : inv
                );
                ctx.patchState({ 
                    investigaciones,
                    envioActualizado: resultadoActualizado.envio
                });
            }

            if (resultadoActualizado.envio != null) {
                return resultadoActualizado.envio;
            }
        }

        return null;
    }

    @Action(InvestigacionesAcciones.CargarEtiquetas)
    public async cargarEtiquetas(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.CargarEtiquetas
    ) {
        const nuevoState = this.obtenerNuevoState(ctx);
        const investigacionActualizada = await this.crearGrupos(action.etiquetas);
        const investigaciones = this.actualizarEntidades(investigacion =>
            investigacion.id === investigacionActualizada.id ? investigacionActualizada : investigacion
        );
        nuevoState.investigaciones = [
            ...investigaciones.filter(investigacion => this.soloActivas ? investigacion.activado : true)
        ];
        nuevoState.investigacionSeleccionada = investigacionActualizada;
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.ObtenerPreguntas)
    public async obtenerPreguntas(
        ctx: StateContext<IInvestigacionesState>, action: InvestigacionesAcciones.ObtenerPreguntas
    ) {
        const nuevoState = this.obtenerNuevoState(ctx);
        const clonEncuesta = new EncuestaDTO(action.encuesta.toJSON());
        await this.encuestasService.obtenerPreguntas(clonEncuesta);
        const clonInvestigacion = new InvestigacionDTO(action.investigacion.toJSON());
        clonInvestigacion.encuestas = clonInvestigacion.encuestas.map(encuesta =>
            encuesta.id === action.encuesta.id ? clonEncuesta : encuesta);
        nuevoState.investigacionSeleccionada = clonInvestigacion;
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.ReiniciarRespuestas)
    public reiniciarRespuestas(ctx: StateContext<IInvestigacionesState>) {
        const nuevoState = this.obtenerNuevoState(ctx);
        nuevoState.marcadoresInvestigacionSeleccionada = null;
        nuevoState.respuestasDiaInvestigacionSeleccionada = null;
        nuevoState.respuestasEncuestaInvestigacionSeleccionada = null;
        ctx.setState(nuevoState);
    }

    @Action(InvestigacionesAcciones.Salir)
    public salir(ctx: StateContext<IInvestigacionesState>) {
        this.limpiarTodo();
        ctx.setState({
            totalInvestigaciones: 0,
            investigaciones: [],
            investigacionSeleccionada: null,
            cuotasInvestigacionSeleccionada: null,
            funnelsInvestigacionSeleccionada: null,
            marcadoresInvestigacionSeleccionada: null,
            respuestasDiaInvestigacionSeleccionada: null,
            respuestasEncuestaInvestigacionSeleccionada: null,
            gruposInvestigacionSeleccionada: null,
            envioActualizado: null
        });
    }

    @Action(InvestigacionesAcciones.SacarSeleccionada)
    public sacarSeleccionada(ctx: StateContext<IInvestigacionesState>) {
        ctx.patchState({
            investigacionSeleccionada: null
        });
    }

    private async crearGrupos(tags: TagDTO[]): Promise<InvestigacionDTO> {
        let investigacionActualizada = new InvestigacionDTO();
        await Promise.sequence(this.paisesInvestigacionNueva!.map(async pais => {
            const grupoNuevo = new GrupoDTO({ name: `Grupo ${pais.nombre}`, target_country: pais.codigo });
            const actualizadorEncontrado = obtenerActualizadores(
                this.investigacionesService, this.gruposFiltroService, this.respuestasService
            ).find(actualizador => actualizador.esActualizador({
                datosGrupo: { grupo: grupoNuevo, accion: AccionEnum.AGREGAR }
            }));
            if (actualizadorEncontrado == null) {
                return;
            }
            investigacionActualizada = await actualizadorEncontrado.actualizar(
                this.investigacionNueva!, { datosGrupo: { grupo: grupoNuevo, accion: AccionEnum.AGREGAR } }
            );
            const tagPais = tags.find(tag => tag.valor === pais.codigo);

            if (tagPais == null) {
                return;
            }

            const grupo = investigacionActualizada.grupos.find(grupoDTO => grupoDTO.nombre === `Grupo ${pais.nombre}`)!;
            const grupoFiltro = new GrupoFiltroDTO();
            grupoFiltro.asignarNombre(this.investigacionNueva!, grupo);
            grupoFiltro.grupo = grupo;
            grupoFiltro.targetPais = pais;
            grupoFiltro.tags = [tagPais];
            const grupoFiltroDTO = await this.gruposFiltroService.agregarGrupoFiltro(grupoFiltro);
            grupo.gruposFiltros.push(grupoFiltroDTO);
        }));
        return investigacionActualizada;
    }

    private static reiniciarExtras(stateNuevo: IInvestigacionesState) {
        stateNuevo.cuotasInvestigacionSeleccionada = null;
        stateNuevo.funnelsInvestigacionSeleccionada = null;
        stateNuevo.marcadoresInvestigacionSeleccionada = null;
    }

    private iniciarFetchRespuestasWorker(
        ctx: StateContext<IInvestigacionesState>, nuevoState: IInvestigacionesState, investigacion: InvestigacionDTO,
        encuesta: EncuestaDTO, index: number
    ) {
        const worker = new Worker(new URL("../../../shared/workers/fetch-respuestas.worker", import.meta.url), {
            type: "module",
            name: "fetch-respuestas"
        });
        worker.onmessage = ({ data }) => {
            const respuestas = new Map(nuevoState.respuestasEncuestaInvestigacionSeleccionada!.entries());
            respuestas.set(encuesta.id, data);
            nuevoState.respuestasEncuestaInvestigacionSeleccionada = respuestas;
            ctx.setState(nuevoState);
        };
        worker.postMessage({
            investigacionId: investigacion.id,
            encuestaId: encuesta.id,
            cantidadPreguntas: null,
            index,
            token: localStorage.getItem("token")
        });
    }
}
